/**
 * Public Signing Page (A1, B4)
 *
 * Unauthenticated page for external signers to sign via one-time token link.
 * Route: /sign/:token
 *
 * Flow:
 * 1. Resolve token → load ceremony context
 * 2. Show PDF + "Sign here ↓" indicator at field position
 * 3. Click "Sign" → SignatureCapture modal
 * 4. Submit → recordSignatureViaToken mutation → confirmation
 *
 * No FERP account required.
 *
 * Note: SignatureCapture's onSign callback receives (dataUrl, signerId).
 * For public signers the signerId comes from the token payload (participantEmail).
 */

import { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { PDFViewer } from '@/components/pdf/PDFViewer';
import { SignatureCapture } from '@/components/pdf/SignatureCapture';

// COMPLETE_CEREMONY_FIELD: canonical mutation from useCeremonyFieldCompletion hook
import { COMPLETE_CEREMONY_FIELD } from '@/hooks/useCeremonyFieldCompletion';

import {
  useResolveSigningTokenQuery,
  useRequestDocumentUrlViaTokenQuery,
  useRecordSignatureViaTokenMutation,
} from '@/generated/graphql';
import {
  SigningGuidedFlow,
  SigningFieldOverlay,
  type GuidedField,
} from '@/components/pdf/SigningGuidedFlow';
import {
  IconSignature,
  IconCheck,
  IconAlertCircle,
  IconClock,
  IconFileText,
} from '@tabler/icons-react';
import {
  Box,
  Container,
  Stack,
  Title,
  Text,
  Button,
  Alert,
  Paper,
  Group,
  Loader,
  Center,
  Badge,
  ThemeIcon,
  Stepper,
} from '@mantine/core';
interface CeremonyBranding {
  logoUrl?: string | null;
  companyName?: string | null;
  primaryColor?: string | null;
}

interface TokenPayload {
  ceremonyId: string;
  participantIndex: number;
  participantName: string;
  participantEmail: string;
  documentName: string;
  documentId: string;
  tenantId: string;
  expiresAt?: string | null;
  alreadySigned: boolean;
  fieldPage?: number | null;
  fieldX?: number | null;
  fieldY?: number | null;
  fieldWidth?: number | null;
  fieldHeight?: number | null;
  returnUrl?: string | null;
  branding?: CeremonyBranding | null;
  fields?: Array<{
    fieldIndex: number;
    participantIndex: number;
    fieldType: string;
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
    completed: boolean;
  }> | null;
}

interface ResolveTokenData {
  resolveSigningToken: TokenPayload | null;
}

export function PublicSigningPage() {
  const { token } = useParams<{ token: string }>();
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);

  const { data, loading, error } = useResolveSigningTokenQuery({
    variables: { token: token ?? '' },
    skip: !token,
    fetchPolicy: 'network-only',
  });

  const { data: docUrlData } = useRequestDocumentUrlViaTokenQuery({
    variables: { token: token ?? '' },
    // Only fetch once token payload is confirmed valid and not already signed
    skip: !token || !data?.resolveSigningToken || data.resolveSigningToken.alreadySigned,
    fetchPolicy: 'network-only',
  });

  const [recordSignature] = useRecordSignatureViaTokenMutation();
  const [completeField, { loading: completingField }] = useMutation(COMPLETE_CEREMONY_FIELD);

  const payload = data?.resolveSigningToken as TokenPayload | null | undefined;

  // Build guided fields from token payload
  const guidedFields: GuidedField[] = useMemo(() => {
    if (!payload?.fields || payload.fields.length === 0) return [];
    return payload.fields.map((f) => ({
      fieldIndex: f.fieldIndex,
      participantIndex: f.participantIndex,
      fieldType: f.fieldType as GuidedField['fieldType'],
      page: f.page,
      x: f.x,
      y: f.y,
      width: f.width,
      height: f.height,
      completed: f.completed,
    }));
  }, [payload?.fields]);

  const hasGuidedFields = guidedFields.length > 0;

  // Handle completing a single field in the step-through flow
  const handleCompleteField = useCallback(
    async (fieldIndex: number, value: string | null) => {
      if (!payload) return;
      await completeField({
        variables: {
          input: {
            ceremonyId: payload.ceremonyId,
            tenantId: payload.tenantId,
            fieldIndex,
            value,
          },
        },
      });
    },
    [payload, completeField],
  );

  const handleSign = async (signatureImage: string, _signerId: string) => {
    if (!token) return;
    setSignError(null);
    setSigning(true);
    try {
      await recordSignature({
        variables: {
          input: {
            token,
            signatureImage,
          },
        },
      });
      setSignatureModalOpen(false);
      // D3: Redirect to returnUrl if set; otherwise show confirmation screen
      if (payload?.returnUrl) {
        window.location.href = payload.returnUrl;
        return;
      }
      setSigned(true);
    } catch (e: unknown) {
      setSignError(e instanceof Error ? e.message : 'Failed to submit signature. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  if (!token) {
    return (
      <Container size="sm" mt="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Invalid Link">
          This signing link is invalid or has expired.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Stack align="center" gap="sm">
          <Loader size="lg" />
          <Text c="dimmed">Loading signing request…</Text>
        </Stack>
      </Center>
    );
  }

  if (error || !payload) {
    return (
      <Container size="sm" mt="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Link Expired or Not Found">
          This signing link has expired, already been used, or was not found.
          Please contact the sender for a new link.
        </Alert>
      </Container>
    );
  }

  if (payload.alreadySigned || signed) {
    return (
      <Center style={{ minHeight: '100vh', background: 'var(--mantine-color-gray-0)' }}>
        <Container size="sm">
          <Paper p="xl" withBorder radius="md" shadow="sm">
            <Stack align="center" gap="lg">
              {/* Large confirmation icon — easy to see on tablet */}
              <ThemeIcon size={80} color="green" radius="50%">
                <IconCheck size={48} />
              </ThemeIcon>
              <Title order={2} ta="center">All Done!</Title>
              <Text size="lg" ta="center">
                You've signed <strong>{payload.documentName}</strong>.
              </Text>
              {signed && (
                <Text c="dimmed" ta="center">
                  The sender has been notified. You'll receive a copy once all parties have signed.
                </Text>
              )}
              {!signed && (
                <Text c="dimmed" ta="center">
                  This document was already signed. No further action needed.
                </Text>
              )}
            </Stack>
          </Paper>
        </Container>
      </Center>
    );
  }

  const pdfUrl = docUrlData?.requestDocumentUrlViaToken?.downloadUrl ?? '';

  const hasFieldPosition =
    payload.fieldPage != null &&
    payload.fieldX != null &&
    payload.fieldY != null;

  // D6: Apply branding primary color as a CSS variable on the root element
  const brandingStyle = payload.branding?.primaryColor
    ? ({ '--ferp-signing-primary': payload.branding.primaryColor } as React.CSSProperties)
    : {};

  const pageTitle = payload.branding?.companyName ?? 'Sign Document';
  const signButtonColor = payload.branding?.primaryColor ?? undefined;

  return (
    <Box style={{ minHeight: '100vh', background: 'var(--mantine-color-gray-0)', ...brandingStyle }}>
      {/* Header */}
      <Box
        style={{
          background: 'white',
          borderBottom: '1px solid var(--mantine-color-gray-3)',
          padding: '16px 24px',
        }}
      >
        <Group justify="space-between">
          <Stack gap={4}>
            {/* D6: Show logo if provided */}
            {payload.branding?.logoUrl && (
              <img
                src={payload.branding.logoUrl}
                alt={pageTitle}
                style={{ maxHeight: 48, maxWidth: 180, objectFit: 'contain' }}
              />
            )}
            <Title order={4}>{payload.documentName}</Title>
            <Text size="sm" c="dimmed">
              {payload.participantName}
            </Text>
          </Stack>
          {payload.expiresAt && (
            <Badge color="orange" leftSection={<IconClock size={12} />} size="lg">
              Expires {new Date(payload.expiresAt).toLocaleDateString()}
            </Badge>
          )}
        </Group>
      </Box>

      {/* Progress stepper — gives tablet users a clear sense of where they are */}
      <Box style={{ background: 'white', borderBottom: '1px solid var(--mantine-color-gray-2)', padding: '12px 24px' }}>
        <Container size="sm">
          <Stepper active={signatureModalOpen ? 1 : 0} size="sm" color="blue">
            <Stepper.Step
              label="Review"
              description="Read the document"
              icon={<IconFileText size={16} />}
            />
            <Stepper.Step
              label="Sign"
              description="Add your signature"
              icon={<IconSignature size={16} />}
            />
            <Stepper.Step
              label="Done"
              description="Signature recorded"
              icon={<IconCheck size={16} />}
            />
          </Stepper>
        </Container>
      </Box>

      {/* Main */}
      <Container size="lg" py="lg">
        <Stack gap="md">
          {signError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title="Signature failed">
              {signError}
            </Alert>
          )}

          {/* Step-through guided signing flow (when multiple fields placed) */}
          {hasGuidedFields ? (
            <>
              <Alert color="blue" icon={<IconSignature size={16} />}>
                Complete each field below. The document shows where each field is located.
              </Alert>

              {/* PDF viewer with field position overlays */}
              <Paper
                withBorder
                style={{ position: 'relative', overflow: 'hidden', touchAction: 'pan-y' }}
              >
                <PDFViewer
                  url={pdfUrl}
                  documentId={payload.documentId}
                  annotations={[]}
                  onAnnotationsChange={() => {}}
                  canAnnotate={false}
                  canSign={false}
                />
                <SigningFieldOverlay
                  fields={guidedFields}
                  activeFieldIndex={
                    guidedFields.find((f) => !f.completed)?.fieldIndex ?? -1
                  }
                  page={1}
                />
              </Paper>

              {/* Guided step-through flow */}
              <SigningGuidedFlow
                fields={guidedFields}
                onCompleteField={handleCompleteField}
                onAllCompleted={() => {
                  if (payload?.returnUrl) {
                    window.location.href = payload.returnUrl;
                    return;
                  }
                  setSigned(true);
                }}
                documentName={payload.documentName}
                submitting={completingField}
                signerName={payload.participantName}
              />

              <Text size="xs" c="dimmed" ta="center">
                Your electronic signature is legally binding and secured with PAdES cryptographic signing.
              </Text>
            </>
          ) : (
            <>
              {/* Legacy single-field signing flow */}
              {!hasFieldPosition && (
                <Alert color="blue" icon={<IconFileText size={16} />}>
                  Review the document below, then tap <strong>Sign Document</strong> to add your signature.
                </Alert>
              )}

              {hasFieldPosition && (
                <Alert color="blue" icon={<IconSignature size={16} />}>
                  Scroll to the <strong>Sign here</strong> box in the document, then tap it — or tap the button below.
                </Alert>
              )}

              <Paper
                withBorder
                style={{ position: 'relative', overflow: 'hidden', touchAction: 'pan-y' }}
              >
                <PDFViewer
                  url={pdfUrl}
                  documentId={payload.documentId}
                  annotations={[]}
                  onAnnotationsChange={() => {}}
                  canAnnotate={false}
                  canSign={false}
                />

                {hasFieldPosition && (
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label="Tap here to sign"
                    style={{
                      position: 'absolute',
                      left: `${(payload.fieldX ?? 0) * 100}%`,
                      top: `${(payload.fieldY ?? 0) * 100}%`,
                      width: `${(payload.fieldWidth ?? 0.15) * 100}%`,
                      minHeight: 48,
                      height: `${Math.max((payload.fieldHeight ?? 0.04) * 100, 6)}%`,
                      border: '2px solid var(--mantine-color-blue-5)',
                      background: 'rgba(51, 154, 240, 0.15)',
                      animation: 'ferp-pulse 1.5s ease-in-out infinite',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                      touchAction: 'manipulation',
                    }}
                    onClick={() => setSignatureModalOpen(true)}
                    onKeyDown={(e) => e.key === 'Enter' && setSignatureModalOpen(true)}
                  >
                    <Text
                      style={{
                        color: 'var(--mantine-color-blue-7)',
                        fontWeight: 700,
                        fontSize: 14,
                        userSelect: 'none',
                      }}
                    >
                      ✍ Tap to sign
                    </Text>
                  </Box>
                )}
              </Paper>

              <Group justify="center">
                <Button
                  size="xl"
                  leftSection={<IconSignature size={24} />}
                  onClick={() => setSignatureModalOpen(true)}
                  loading={signing}
                  fullWidth
                  maw={400}
                  style={{
                    touchAction: 'manipulation',
                    ...(signButtonColor ? { backgroundColor: signButtonColor } : {}),
                  }}
                >
                  Sign Document
                </Button>
              </Group>

              <Text size="xs" c="dimmed" ta="center">
                Your electronic signature is legally binding and secured with PAdES cryptographic signing.
              </Text>

              <SignatureCapture
                opened={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                onSign={handleSign}
                documentName={payload.documentName}
                signing={signing}
              />
            </>
          )}
        </Stack>
      </Container>

      {/* CSS animations */}
      <style>{`
        @keyframes ferp-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(51, 154, 240, 0.4); }
          50% { opacity: 0.7; box-shadow: 0 0 0 8px rgba(51, 154, 240, 0); }
        }
      `}</style>
    </Box>
  );
}
